import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

/**
 * Owns the editable widget list for a report view: a local working copy of the
 * order (so dragging is smooth), reseeding from the persisted config, and the
 * reorder/remove handlers that persist back optimistically. Pulling this out of
 * ReportView keeps that view focused on fetching + synthesis + rendering rather
 * than carrying the drag-and-drop state. Colocated with the Reports views that
 * consume it.
 *
 * `report.config.widgets` stays the source of truth; the working copy is written
 * back on drop (reorder) and on remove. Pass `isEditing` so a background refetch
 * never reseeds — and yanks the array — mid-drag.
 *
 * @returns {{ widgets, handleMove, handleDropPersist, handleRemove, handleAdd,
 *   handleUpdate, handleDuplicate }}
 */
const useReportWidgets = ({ report, isEditing, save }) => {
    const persistedWidgets = report?.config?.widgets ?? [];
    const [widgets, setWidgets] = useState([]);

    // Reseed by id-signature (not array identity): every optimistic update yields
    // a fresh `report` object, so keying on the id list avoids reseeding on each
    // refetch. The first seed always runs (so an always-editing report still loads
    // its widgets); after that we never reseed mid-edit — a background refetch
    // would yank the working-copy order during a drag.
    const persistedSig = persistedWidgets.map((w) => w.id).join('|');
    const seededSigRef = useRef(null);
    useEffect(() => {
        if (!report) return; // wait for the report before the first seed
        if (persistedSig === seededSigRef.current) return;
        if (isEditing && seededSigRef.current !== null) return;
        setWidgets(persistedWidgets);
        seededSigRef.current = persistedSig;
        // persistedWidgets read fresh; persistedSig is the change signal.
    }, [report, persistedSig, isEditing]);

    // Persist ONLY the widgets field (and clear analysis when emptied) as an
    // updater on the saver's latest config — never spread the lagging
    // `report.config`, which would write back a stale `analysis` and race the
    // streamed synthesis. Building on `prev` lets concurrent saves accumulate.
    const persistWidgets = useCallback(
        (nextWidgets) => {
            if (!report) return;
            const emptied = nextWidgets.length === 0;
            save((prev) => ({
                ...prev,
                widgets: nextWidgets,
                // A report with no widgets has nothing to analyze.
                ...(emptied ? { analysis: null } : {}),
            }));
        },
        [report, save],
    );

    const handleMove = useCallback((dragIdx, hoverIdx) => {
        setWidgets((prev) => {
            const next = prev.slice();
            const [moved] = next.splice(dragIdx, 1);
            next.splice(hoverIdx, 0, moved);
            return next;
        });
    }, []);

    // Reorder is order-independent for the synthesis hash, so persisting it never
    // triggers re-synthesis. Persist once on drop, not on every hover-swap.
    const handleDropPersist = useCallback(() => {
        setWidgets((current) => {
            persistWidgets(current);
            return current;
        });
    }, [persistWidgets]);

    const handleRemove = useCallback(
        (widgetId) => {
            setWidgets((prev) => {
                const next = prev.filter((w) => w.id !== widgetId);
                persistWidgets(next);
                return next;
            });
        },
        [persistWidgets],
    );

    // Append harvested widget configs. Updates the working copy directly (not via
    // the persisted-config reseed, which is gated off mid-edit) so a widget added
    // while editing shows immediately — same pattern as handleRemove.
    const handleAdd = useCallback(
        (newWidgets) => {
            if (!newWidgets?.length) return;
            setWidgets((prev) => {
                const next = [...prev, ...newWidgets];
                persistWidgets(next);
                return next;
            });
        },
        [persistWidgets],
    );

    // Replace one widget's args in place (the edit-modal Save). The id is kept, so
    // its slot/order is unchanged; the args change re-keys its widgetQuery so the
    // grid refetches the new config. `meta` is the complete sibling set (today
    // just `title`): a key omitted from it is cleared.
    const handleUpdate = useCallback(
        (widgetId, newArgs, meta) => {
            setWidgets((prev) => {
                const next = prev.map((w) => {
                    if (w.id !== widgetId) return w;
                    const updated = { ...w, args: newArgs };
                    if (meta?.title) updated.title = meta.title;
                    else delete updated.title;
                    return updated;
                });
                persistWidgets(next);
                return next;
            });
        },
        [persistWidgets],
    );

    // Clone a widget and insert the copy right after the original. A fresh uuid
    // keeps React keys + the reseed id-signature correct; since widgetQuery keys
    // on args (not id), the copy shares the cache and shows data immediately.
    const handleDuplicate = useCallback(
        (widgetId) => {
            setWidgets((prev) => {
                const i = prev.findIndex((w) => w.id === widgetId);
                if (i === -1) return prev;
                const src = prev[i];
                const copy = {
                    ...src,
                    id: uuid(),
                    args: {
                        ...src.args,
                        ...(src.args?.filters
                            ? { filters: { ...src.args.filters } }
                            : {}),
                        ...(src.args?.entity_ids
                            ? { entity_ids: [...src.args.entity_ids] }
                            : {}),
                        ...(src.args?.hashtags
                            ? { hashtags: [...src.args.hashtags] }
                            : {}),
                        ...(src.args?.exclude
                            ? {
                                  exclude: src.args.exclude.map((t) => ({
                                      ...t,
                                  })),
                              }
                            : {}),
                    },
                };
                const next = [
                    ...prev.slice(0, i + 1),
                    copy,
                    ...prev.slice(i + 1),
                ];
                persistWidgets(next);
                return next;
            });
        },
        [persistWidgets],
    );

    return {
        widgets,
        handleMove,
        handleDropPersist,
        handleRemove,
        handleAdd,
        handleUpdate,
        handleDuplicate,
    };
};

export default useReportWidgets;
